"use client"

import { Component, Suspense, useEffect, useState } from "react"
import type { ReactNode } from "react"
import dynamic from "next/dynamic"
import Link from "next/link"
import { usePathname, useRouter, useSearchParams } from "next/navigation"
import { useAuth } from "@clerk/nextjs"
import { Menu, X } from "lucide-react"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"
import {
  AUTHENTICATED_HOME_PATH,
  PUBLIC_LANDING_PATH,
} from "@/lib/routing/home"
import {
  SITE_CHROME_LANDING_HOST_PILL_CLASS,
  SITE_CHROME_LANDING_SIGN_IN_QUIET_CLASS,
  SITE_CHROME_OUTLINE_BUTTON_CLASS,
  SITE_CHROME_PRIMARY_LINK_CLASS,
  SITE_CHROME_PUBLIC_SIGN_IN_CLASS,
} from "@/lib/chrome/site-chrome"
import {
  EVENT_GUEST_PREVIEW_QUERY_KEY,
  EVENT_GUEST_PREVIEW_QUERY_VALUE,
} from "@/lib/events/urls"
import { MaatiiChromeBrandLockup } from "@/components/layout/maatii-chrome-brand-lockup"

/** Code-split: signed-out users never download bell + notifications hook graph. */
const NotificationBell = dynamic(
  () =>
    import("@/components/shells/notification-bell").then((m) => ({
      default: m.NotificationBell,
    })),
  {
    loading: () => (
      <span
        className="inline-flex size-[var(--control-height-button)] shrink-0 items-center justify-center rounded-full"
        aria-hidden
      />
    ),
  },
)

/** Code-split: menu + `useUser` / `useClerk` stay off the signed-out bundle. */
const UserDropdown = dynamic(
  () =>
    import("@/components/user-dropdown").then((m) => ({
      default: m.UserDropdown,
    })),
  {
    loading: () => (
      <span
        className="inline-flex size-[var(--control-height-button)] shrink-0 items-center justify-center rounded-full"
        aria-hidden
      />
    ),
  },
)

/** Code-split: modal sign-in UI loads in its own chunk until this renders. */
const SignInButton = dynamic(
  () => import("@clerk/nextjs").then((m) => m.SignInButton),
  {
    loading: () => (
      <Button
        type="button"
        variant="ghost"
        className={SITE_CHROME_PUBLIC_SIGN_IN_CLASS}
        disabled
        aria-busy
      >
        Sign in
      </Button>
    ),
  },
)

const SITE_CHROME_HEADER_BASE =
  "site-chrome-header site-chrome-header--auth-safe sticky top-0 z-[var(--z-overlay)] w-full border-b border-border/60 bg-background/95 backdrop-blur-xl md:border-border/40"

const SITE_CHROME_HEADER_LANDING_FLOAT =
  "border-transparent bg-transparent backdrop-blur-[5px] supports-[backdrop-filter]:bg-[color-mix(in_oklab,var(--background)_8%,transparent)] md:border-border/20 md:backdrop-blur-md md:supports-[backdrop-filter]:bg-[color-mix(in_oklab,var(--background)_12%,transparent)]"

type HeaderActionsBoundaryState = { hasError: boolean }

class HeaderChromeActionsErrorBoundary extends Component<
  { children: ReactNode },
  HeaderActionsBoundaryState
> {
  constructor(props: { children: ReactNode }) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError(): HeaderActionsBoundaryState {
    return { hasError: true }
  }

  render() {
    if (this.state.hasError) {
      return (
        <>
          <span
            className="inline-flex size-[var(--control-height-button)] shrink-0 items-center justify-center rounded-full"
            aria-hidden
          />
          <span
            className="inline-flex size-[var(--control-height-button)] shrink-0 items-center justify-center rounded-full"
            aria-hidden
          />
        </>
      )
    }
    return this.props.children
  }
}

/** Skeleton for the actions column only — brand and nav chrome are visible even while Clerk loads. */
function ActionsLoadingSkeleton() {
  return (
    <span
      className="inline-flex h-9 min-h-[2.75rem] w-[4.75rem] shrink-0 animate-pulse rounded-md bg-muted"
      aria-hidden
    />
  )
}

type AppHeaderChromeProps = {
  /** When `useSearchParams` is still resolving, assume false so the primary chrome renders. */
  eventGuestPreviewActive: boolean
}

function AppHeaderChrome({
  eventGuestPreviewActive,
}: AppHeaderChromeProps) {
  const pathname = usePathname()
  const router = useRouter()
  const { isSignedIn, isLoaded } = useAuth()
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

  /**
   * Sheet UI is `md:hidden`; only lock document scroll when the overlay is actually shown.
   * Otherwise resizing to desktop with menu state still "open" left `body` stuck with overflow hidden.
   */
  useEffect(() => {
    if (typeof window === "undefined") return
    const mq = window.matchMedia("(max-width: 767px)")
    const sync = () => {
      document.body.style.overflow = (mobileMenuOpen && mq.matches) ? "hidden" : ""
    }
    sync()
    mq.addEventListener("change", sync)
    return () => {
      mq.removeEventListener("change", sync)
      document.body.style.overflow = ""
    }
  }, [mobileMenuOpen])

  useEffect(() => {
    if (!mobileMenuOpen) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setMobileMenuOpen(false)
    }
    window.addEventListener("keydown", onKey)
    return () => window.removeEventListener("keydown", onKey)
  }, [mobileMenuOpen])

  const navItems = [
    { label: "Events", href: "/events" },
    { label: "Archive", href: "/timeline" },
    { label: "Memories", href: "/memories" },
  ] as const

  const isActive = (href: string) => {
    if (href === "/events") {
      return (
        pathname === "/events" ||
        pathname.startsWith("/events/") ||
        pathname === "/attending" ||
        pathname.startsWith("/attending/")
      )
    }
    return pathname.startsWith(href)
  }

  if (pathname === "/story" || pathname.startsWith("/story/")) {
    return null
  }

  /** Full-page Clerk auth — avoid duplicating “Sign in” next to the embedded form. */
  const isFullPageClerkAuth =
    pathname === "/sign-in" ||
    pathname.startsWith("/sign-in/") ||
    pathname === "/sign-up" ||
    pathname.startsWith("/sign-up/")

  /** Marketing `/` — token-driven landing bar (layout + surface); not gated on Clerk load. */
  const landingPathVisual = pathname === PUBLIC_LANDING_PATH

  /** Signed-out marketing home — action cluster (Sign in + Host an event). */
  const marketingLandingHeaderChrome =
    isLoaded && !isSignedIn && landingPathVisual

  const eventIdFromGuestPreviewPath =
    eventGuestPreviewActive && pathname.startsWith("/event/")
      ? pathname.slice("/event/".length).split("/")[0]?.trim() ?? ""
      : ""

  const closeGuestPreview = () => {
    if (typeof window !== "undefined" && window.opener && !window.opener.closed) {
      window.close()
      return
    }
    if (eventIdFromGuestPreviewPath) {
      router.push(`/edit/${eventIdFromGuestPreviewPath}`)
      return
    }
    router.push(AUTHENTICATED_HOME_PATH)
  }

  if (isLoaded && isSignedIn && eventGuestPreviewActive) {
    return (
      <>
        <header className={SITE_CHROME_HEADER_BASE}>
          <div
            className={cn(
              "app-shell chrome-header-bar chrome-header-bar--two-col",
            )}
          >
            <div className="chrome-header-brand gap-[var(--space-sm)] sm:gap-[var(--space-md)]">
              <MaatiiChromeBrandLockup href={PUBLIC_LANDING_PATH} />
              <span className="type-caption hidden text-muted-foreground sm:inline">
                Guest preview
              </span>
            </div>
            <div className="chrome-header-actions">
              <Button
                type="button"
                variant="outline"
                size="sm"
                className={cn(SITE_CHROME_OUTLINE_BUTTON_CLASS, "shrink-0 rounded-full")}
                onClick={closeGuestPreview}
              >
                Close
              </Button>
            </div>
          </div>
        </header>
      </>
    )
  }

  return (
    <>
      <header
        className={cn(
          "site-chrome-header sticky top-0 z-[var(--z-overlay)] w-full border-b",
          landingPathVisual
            ? SITE_CHROME_HEADER_LANDING_FLOAT
            : "border-border/60 bg-background/95 backdrop-blur-xl md:border-border/40",
          landingPathVisual && "site-chrome-header--landing",
          isLoaded && isSignedIn && "site-chrome-header--auth-safe",
          isLoaded &&
            isSignedIn &&
            mobileMenuOpen &&
            "max-md:z-[110] max-md:border-none max-md:bg-background max-md:shadow-none",
        )}
      >
        <div
          className={cn(
            "app-shell chrome-header-bar",
            isLoaded && !isSignedIn && "chrome-header-bar--two-col",
          )}
          suppressHydrationWarning
        >
          {/* Brand: always visible — never skeletonized so the header is never blank */}
          <div className="chrome-header-brand">
            <MaatiiChromeBrandLockup
              href={isLoaded && isSignedIn ? AUTHENTICATED_HOME_PATH : PUBLIC_LANDING_PATH}
            />
          </div>

          {/* Nav: occupies center grid column; empty placeholder keeps layout stable while loading */}
          {isLoaded && isSignedIn ? (
            <nav className="chrome-header-nav" aria-label="Primary">
              {navItems.map((item) => (
                <Link
                  key={item.href}
                  href={item.href}
                  className={cn(
                    "type-chrome-nav-link",
                    isActive(item.href) && "type-chrome-nav-link--active",
                  )}
                >
                  {item.label}
                </Link>
              ))}
            </nav>
          ) : (
            <div className="chrome-header-nav" aria-hidden="true" />
          )}

          {/* Actions: skeleton only for this column while Clerk loads */}
          <div className="chrome-header-actions">
            {!isLoaded ? (
              <ActionsLoadingSkeleton />
            ) : isSignedIn ? (
              <>
                <Link
                  href="/create"
                  className={cn(SITE_CHROME_PRIMARY_LINK_CLASS, "hidden sm:inline-flex")}
                >
                  + Create
                </Link>

                <Button
                  variant="ghost"
                  size="icon"
                  className="size-11 min-h-11 min-w-11 shrink-0 rounded-full text-foreground md:size-10 md:min-h-0 md:min-w-0 md:hidden"
                  type="button"
                  onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                  aria-expanded={mobileMenuOpen}
                  aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
                >
                  {mobileMenuOpen ? (
                    <X className="size-5" />
                  ) : (
                    <Menu className="size-5" />
                  )}
                </Button>

                <HeaderChromeActionsErrorBoundary>
                  <NotificationBell />
                  <UserDropdown />
                </HeaderChromeActionsErrorBoundary>
              </>
            ) : marketingLandingHeaderChrome ? (
              !isFullPageClerkAuth ? (
                <div className="inline-flex max-w-full items-center gap-[var(--chrome-landing-actions-cluster-gap)] whitespace-nowrap">
                  <SignInButton
                    mode="modal"
                    forceRedirectUrl="/events"
                    signUpForceRedirectUrl="/events"
                  >
                    <Button
                      type="button"
                      variant="ghost"
                      className={SITE_CHROME_LANDING_SIGN_IN_QUIET_CLASS}
                    >
                      Sign in
                    </Button>
                  </SignInButton>
                  <SignInButton
                    mode="modal"
                    forceRedirectUrl="/create"
                    signUpForceRedirectUrl="/create"
                  >
                    <button type="button" className={SITE_CHROME_LANDING_HOST_PILL_CLASS}>
                      Host an event
                    </button>
                  </SignInButton>
                </div>
              ) : null
            ) : (
              <>
                {!isFullPageClerkAuth && (
                  <SignInButton
                    mode="modal"
                    forceRedirectUrl="/events"
                    signUpForceRedirectUrl="/events"
                  >
                    <Button
                      type="button"
                      variant="ghost"
                      className={SITE_CHROME_PUBLIC_SIGN_IN_CLASS}
                    >
                      Sign in
                    </Button>
                  </SignInButton>
                )}
              </>
            )}
          </div>
        </div>
      </header>

      {isLoaded && isSignedIn && mobileMenuOpen && (
        <>
          {/* SheetOverlay analogue — below SheetContent (z-[100]), above page chrome */}
          <div
            className="fixed inset-0 z-[90] bg-black/30 backdrop-blur-sm md:hidden"
            aria-hidden
            onClick={() => setMobileMenuOpen(false)}
          />
          {/*
           * Full-viewport sheet (inset-0) + padding-top for nav — not top-offset positioning.
           * A gap between header bottom and a top-positioned sheet exposed z-90 scrim (brown/gray stripe).
           */}
          <div
            className="site-chrome-header fixed inset-0 z-[100] isolate flex min-h-0 flex-col overflow-hidden bg-background md:hidden"
            role="dialog"
            aria-modal="true"
            aria-label="Primary navigation"
          >
            <div className="app-shell flex h-full min-h-0 w-full flex-1 flex-col overflow-y-auto overscroll-contain pt-[calc(var(--spacing-mobile-app-header-offset)+env(safe-area-inset-top,0px)+0.75rem)] pb-[max(0.5rem,env(safe-area-inset-bottom,0px))]">
                <nav className="flex flex-col gap-[var(--space-2xs)]" aria-label="Primary">
                  {navItems.map((item) => (
                    <Link
                      key={item.href}
                      href={item.href}
                      onClick={() => setMobileMenuOpen(false)}
                      className={cn(
                        "type-mobile-nav-link rounded-card-shell px-[var(--space-md)] py-[var(--space-sm)]",
                        isActive(item.href)
                          ? "type-mobile-nav-link--active bg-secondary"
                          : "hover:bg-secondary/50",
                      )}
                    >
                      {item.label}
                    </Link>
                  ))}
                </nav>

                <div className="mt-8">
                  <Button
                    variant="inverse"
                    asChild
                    className="type-button type-weight-semibold h-[var(--control-height-input)] w-full rounded-full"
                  >
                    <Link href="/create" onClick={() => setMobileMenuOpen(false)}>
                      + Create event
                    </Link>
                  </Button>
                </div>
            </div>
          </div>
        </>
      )}
    </>
  )
}

/**
 * `useSearchParams()` can suspend indefinitely in some production navigations; keep it out of the
 * main chrome tree so the header never sticks on the skeleton fallback.
 */
function AppHeaderSearchParamsBridge() {
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const { isSignedIn } = useAuth()
  const eventGuestPreviewActive =
    Boolean(isSignedIn) &&
    pathname.startsWith("/event/") &&
    searchParams.get(EVENT_GUEST_PREVIEW_QUERY_KEY) ===
      EVENT_GUEST_PREVIEW_QUERY_VALUE
  return <AppHeaderChrome eventGuestPreviewActive={eventGuestPreviewActive} />
}

export default function AppHeader() {
  return (
    <Suspense fallback={<AppHeaderChrome eventGuestPreviewActive={false} />}>
      <AppHeaderSearchParamsBridge />
    </Suspense>
  )
}
